import {
    Anchor,
    Button,
    Checkbox,
    Flex,
    Group,
    Input,
    Loader,
    MultiSelect,
    Progress,
    Space,
    Stack,
    Text,
    TextInput,
    Title
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm, yupResolver } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import {
    DayChipGroup,
    FILE_TYPE_OPTIONS,
    FileTypeEnum,
    FileUpload,
    formatPhoneNumber,
    FormFileEntry,
    generateVideoThumbnail,
    getFileTypeFromMimeType,
    ImageWithPlaceholder,
    PageLoadIndicator,
    PriorityEntryInitialValues,
    PriorityEntrySchema,
    PublicFileTypeEnum,
    removeNullValues,
    showErrorNotification,
    showSuccessNotification,
    TIMES,
    titleCase,
    UnifiedPriorityEntryInput,
    useCreatePublicFileRecordMutation,
    useCreatePublicUnifiedPriorityEntryMutation,
    useCreatePublicUploadFilePresignedUrlMutation,
    useGetPublicInsuranceEntitiesQuery,
    useGetPublicInviteLandingEntitiesQuery,
    useGetPublicPriorityEntryDetailsQuery,
    useGetPublicTherapySubCategoryGroupsQuery
} from "@therapprove/common";
import { isSameDay } from "date-fns";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Dialog } from "../components/Dialog";
import { joinQueueLandingBase64ToPayload } from "../utils/payload.utils";

export const JoinQueueLanding = () => {
    // Get the 'key' query string parameter from the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const rawKey = urlParams.get("key");

    // State
    const [pageState, setPageState] = useState<"error" | "success" | null>(
        null
    );
    const [fileUploadFailed, setFileUploadFailed] = useState(false);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [
        areTermsAndPrivacyPolicyChecked,
        setAreTermsAndPrivacyPolicyChecked
    ] = useState(false);
    const [searchInsurances, setSearchInsurances] = useState("");
    const [debouncedSearchInsurances] = useDebouncedValue(
        searchInsurances,
        500
    );
    const [activeStep, setActiveStep] = useState(0);
    const [formFiles, setFormFiles] = useState<FormFileEntry[]>([]);

    // Mutations
    const [, createPublicUnifiedPriorityEntry] =
        useCreatePublicUnifiedPriorityEntryMutation();
    const [, createPresignedUrl] =
        useCreatePublicUploadFilePresignedUrlMutation();
    const [, createPublicFileRecord] = useCreatePublicFileRecordMutation();

    // Decode the key.
    const decodedKey = useMemo(() => {
        if (!rawKey) return undefined;

        try {
            // base64 often contains '+' which can be converted to spaces in querystrings
            const normalized = rawKey.replace(/ /g, "+");
            const payload = joinQueueLandingBase64ToPayload(normalized);
            if (!payload) {
                setPageState("error");
                return undefined;
            }

            return payload;
        } catch (e) {
            setPageState("error");
            console.error(e);
            return undefined;
        }
    }, [rawKey]);

    // Form Data
    const form = useForm<UnifiedPriorityEntryInput>({
        mode: "controlled",
        initialValues: PriorityEntryInitialValues,
        validate: yupResolver(PriorityEntrySchema),
        validateInputOnChange: false
    });

    // Cleanup file object URLs
    useEffect(() => {
        return () => {
            formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch the public priority entry details.
    const [{ data, fetching, error }] = useGetPublicPriorityEntryDetailsQuery({
        variables: {
            key1: decodedKey?.key1 ?? "",
            key2: decodedKey?.key2 ?? ""
        },
        pause: !decodedKey || !decodedKey.key1 || !decodedKey.key2
    });

    // Destructure the public priority entry details.
    const publicPriorityEntryDetails =
        data?.priorityEntries?.publicPriorityEntryDetails;
    const organizationId = publicPriorityEntryDetails?.organizationId;
    const organizationName = publicPriorityEntryDetails?.name;
    const imageUrl = publicPriorityEntryDetails?.imageUrl;
    const isExpired = publicPriorityEntryDetails?.isExpired;

    // Fetch the entity data (conditions, insurances, and therapy categories).
    const [{ data: entityData }] = useGetPublicInviteLandingEntitiesQuery({
        variables: {
            organizationId: organizationId ?? ""
        },
        pause: !decodedKey || isExpired || !organizationId
    });

    const [{ data: insuranceData, fetching: insuranceFetching }] =
        useGetPublicInsuranceEntitiesQuery({
            variables: {
                search: debouncedSearchInsurances
            },
            pause: !debouncedSearchInsurances
        });

    // Data
    const insurances = insuranceData?.insurances?.insurancesList;
    const conditions = entityData?.conditions?.conditionsList;
    const therapyCategories =
        entityData?.therapyCategories?.therapyCategoriesList;
    const isButtonDisabled = !areTermsAndPrivacyPolicyChecked;

    // Fetch the therapy sub category groups.
    const [{ data: therapySubCategoryGroups }] =
        useGetPublicTherapySubCategoryGroupsQuery({
            variables: {
                therapyCategoryIds:
                    form.values.patient?.therapyCategoryIds ?? []
            },
            pause: !form.values.patient?.therapyCategoryIds
        });

    // Destructure the therapy sub categories.
    const therapySubCategories =
        therapySubCategoryGroups?.therapySubCategories
            ?.therapySubCategoryGroups;

    const steps: { label: string; error?: string[] }[] = [
        {
            label: "Primary Contact",
            error: [
                "caregiver.firstName",
                "caregiver.lastName",
                "caregiver.email",
                "caregiver.phoneNumber"
            ]
        },
        {
            label: "About Your Child",
            error: [
                "patient.firstName",
                "patient.lastName",
                "patient.dateOfBirth",
                "patient.postalCode",
                "patient.therapyCategoryIds"
            ]
        },
        { label: "Almost Done - Let's Match You" }
    ];

    const getFirstStepWithErrors = (errors: Record<string, unknown>) => {
        for (let i = 0; i < steps.length; i++) {
            const stepErrors = steps[i]?.error ?? [];
            if (stepErrors.some((path) => !!errors[path])) return i;
        }
        return null;
    };

    const additionalStepHasIssues =
        hasAttemptedSubmit && !areTermsAndPrivacyPolicyChecked;

    const stepHasIssues = (stepIndex: number) => {
        if (!hasAttemptedSubmit) return false;
        if (stepIndex === 2) return additionalStepHasIssues;
        const stepErrors = steps[stepIndex]?.error ?? [];
        return stepErrors.some((path) => !!form.errors[path]);
    };

    const handleSetFiles = async (newFiles: File[]) => {
        const entries: FormFileEntry[] = await Promise.all(
            newFiles.map(async (file) => {
                let thumbnailUrl: string | undefined;
                if (file.type.startsWith("video/")) {
                    try {
                        thumbnailUrl = await generateVideoThumbnail(file);
                    } catch (e) {
                        console.warn("Could not generate video thumbnail", e);
                    }
                }

                return {
                    file,
                    url: URL.createObjectURL(file),
                    thumbnailUrl,
                    name: file.name
                        .replace(/\.[^/.]+$/, "")
                        .replace(/[^a-zA-Z0-9]/g, " "),
                    fileType: getFileTypeFromMimeType(file.type) as FileTypeEnum
                };
            })
        );

        setFormFiles((prev) => [...prev, ...entries]);
    };

    const removeFile = (index: number) => {
        setFormFiles((prev) => {
            const next = [...prev];
            URL.revokeObjectURL(next[index].url);
            next.splice(index, 1);
            return next;
        });
    };

    const updateFileField = (
        index: number,
        field: keyof FormFileEntry,
        value: any
    ) => {
        setFormFiles((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], [field]: value };
            return next;
        });
    };

    const clearAllFiles = () => {
        formFiles.forEach((f) => URL.revokeObjectURL(f.url));
        setFormFiles([]);
    };

    const uploadAllFiles = async () => {
        if (!decodedKey?.key1 || !decodedKey?.key2) {
            throw new Error("Invalid link (missing upload keys)");
        }
        if (!formFiles.length) return;

        setIsUploadingFiles(true);
        try {
            await Promise.all(
                formFiles.map(async (f) => {
                    const presignResult = await createPresignedUrl({
                        input: {
                            key1: decodedKey.key1,
                            key2: decodedKey.key2,
                            fileName: f.file.name,
                            fileType:
                                f.fileType as unknown as PublicFileTypeEnum,
                            contentType: f.file.type
                        }
                    });

                    const presigned = (presignResult.data as any)?.files
                        ?.createPublicUploadFilePresignedUrl;
                    const presignedUrl = presigned?.presignedUrl as
                        | string
                        | undefined;
                    const caregiverId = presigned?.caregiverId as
                        | string
                        | undefined;
                    const patientId = presigned?.patientId as
                        | string
                        | undefined;

                    if (!presignedUrl) {
                        throw new Error(
                            (presignResult.error as any)?.message?.replace?.(
                                "[GraphQL]",
                                ""
                            ) || "Failed to get upload URL"
                        );
                    }

                    const uploadResponse = await fetch(presignedUrl, {
                        method: "PUT",
                        body: f.file,
                        headers: { "Content-Type": f.file.type }
                    });

                    if (!uploadResponse.ok) {
                        throw new Error(
                            `File upload failed: ${uploadResponse.statusText}`
                        );
                    }

                    const recordResult = await createPublicFileRecord({
                        input: {
                            key1: decodedKey.key1,
                            key2: decodedKey.key2,
                            name: f.name || f.file.name,
                            fileName: f.file.name,
                            fileType:
                                f.fileType as unknown as PublicFileTypeEnum,
                            contentType: f.file.type,
                            isShared: false,
                            caregiverId,
                            patientId
                        }
                    });

                    const record = (recordResult.data as any)?.files
                        ?.createPublicFileRecord;

                    if (!record?.success) {
                        throw new Error(
                            record?.error || "Failed to create file record"
                        );
                    }
                })
            );

            clearAllFiles();
        } finally {
            setIsUploadingFiles(false);
        }
    };

    const submitPublicPriorityEntry = async (event: FormEvent) => {
        event.preventDefault();

        setHasAttemptedSubmit(true);

        const valid = form.validate();
        if (valid.hasErrors) {
            const firstErrorStep = getFirstStepWithErrors(valid.errors);
            if (firstErrorStep !== null) setActiveStep(firstErrorStep);
            return;
        }
        const formValues = form.getValues();

        removeNullValues(formValues);

        if (!areTermsAndPrivacyPolicyChecked) {
            showErrorNotification(
                "Please accept the terms and conditions and privacy policy."
            );
            setActiveStep(2);
            return;
        } else {
            // If they have accepted the terms and conditions and privacy policy, set the value to true.
            form.setFieldValue("caregiver.agreeToTermsConditions", true);
        }
        if (!decodedKey?.key1 || !decodedKey?.key2) {
            showErrorNotification(
                `This link is invalid, please contact ${organizationName} for a new invite link.`
            );
            return;
        }

        const { data, error } = await createPublicUnifiedPriorityEntry({
            key1: decodedKey?.key1,
            key2: decodedKey?.key2,
            input: formValues
        });

        if (data?.priorityEntries?.createPublicUnifiedPriorityEntry) {
            showSuccessNotification(
                `You're request has been submitted to ${organizationName}!`
            );
            try {
                await uploadAllFiles();
            } catch (e) {
                console.error(e);
                setFileUploadFailed(true);
                showErrorNotification(
                    "Your request was submitted, but we couldn't upload your file(s). Please contact the organization for help."
                );
            } finally {
                setPageState("success");
            }
        }

        if (error) {
            showErrorNotification(
                `An error occurred, please contact ${organizationName} for a new invite link.`
            );
            return;
        }
    };

    // Expired state.
    if (isExpired) {
        return (
            <Dialog
                title="Expired Link"
                description=""
                failureMessage="Sorry, that link is expired, please contact the organization to get another invite link, or contact support@therapprove.com if you believe this is an error."
                icon="undo"
                showAgreement={false}
                isSuccessful={false}
            />
        );
    }

    // Error state.
    if (error || pageState === "error") {
        return (
            <Dialog
                title="Invalid Link"
                description=""
                failureMessage="Sorry, that link is invalid, please contact the organization to get another invite link, or contact support@therapprove.com if you believe this is an error."
                icon="undo"
                showAgreement={false}
                isSuccessful={false}
            />
        );
    }

    // Success state.
    if (pageState === "success") {
        return (
            <Dialog
                title="Thank you!"
                description=""
                successMessage={
                    fileUploadFailed
                        ? `Thank you for onboarding with ${organizationName}! Your request was submitted successfully, but we couldn't upload your file(s). Please contact the organization for help.`
                        : `Thank you for onboarding with ${organizationName}! Please wait for your request to be reviewed and you will be contacted shortly.`
                }
                icon="check"
                showAgreement={false}
                isSuccessful={true}
            />
        );
    }

    if (fetching) {
        return <PageLoadIndicator />;
    }

    return (
        <Stack
            w="100%"
            align="center"
            style={{
                maxWidth: "32rem",
                margin: "0 auto",
                padding: "1rem",
                height: "100%",
                maxHeight: "850px"
            }}
        >
            <Stack
                flex={1}
                mt="md"
                style={{
                    width: "100%",
                    flexGrow: 1
                }}
            >
                <Stack gap={8} mb="lg" style={{ width: "100%" }}>
                    <Flex justify="center" align="center" gap="xs">
                        {imageUrl && (
                            <ImageWithPlaceholder
                                imageUrl={imageUrl}
                                size={40}
                            />
                        )}
                        <Text
                            variant="body"
                            style={{ fontWeight: 600 }}
                        >{`Join ${organizationName}`}</Text>
                    </Flex>
                    <Flex
                        justify="space-between"
                        align="baseline"
                        wrap="nowrap"
                        gap="xs"
                    >
                        <Title
                            order={4}
                            style={{ color: "var(--mantine-color-dark-9)" }}
                        >
                            {steps[activeStep]?.label}
                        </Title>
                        <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                            Step {activeStep + 1} of {steps.length}
                        </Text>
                    </Flex>
                    <Progress
                        value={((activeStep + 1) / steps.length) * 100}
                        color="var(--mantine-color-primary-9)"
                        size="sm"
                        radius="xl"
                        style={{ flex: 1, minHeight: 8 }}
                    />
                </Stack>

                <Space h="xl" />

                {/* STEP 1: PRIMARY CONTACT */}
                {activeStep === 0 && (
                    <Stack gap="sm" align="left">
                        <TextInput
                            data-autofocus
                            required
                            size="md"
                            radius="md"
                            label="First Name"
                            placeholder="First Name"
                            {...form.getInputProps("caregiver.firstName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "caregiver.firstName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            size="md"
                            radius="md"
                            label="Last Name"
                            placeholder="Last Name"
                            {...form.getInputProps("caregiver.lastName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "caregiver.lastName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            size="md"
                            radius="md"
                            label="Email"
                            placeholder="your.email@example.com"
                            {...form.getInputProps("caregiver.email")}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                form.setFieldValue(
                                    "caregiver.email",
                                    event.target.value.trim().toLowerCase()
                                );
                            }}
                        />
                        <TextInput
                            required
                            size="md"
                            radius="md"
                            label="Phone"
                            placeholder="(000) 000-0000"
                            {...form.getInputProps("caregiver.phoneNumber")}
                            onChange={(
                                event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                const formattedNumber = formatPhoneNumber(
                                    event.target.value.trim()
                                );
                                form.setFieldValue(
                                    "caregiver.phoneNumber",
                                    formattedNumber
                                );
                            }}
                        />
                        <TextInput
                            required
                            size="md"
                            radius="md"
                            label="Zip Code"
                            placeholder="We match you with nearby providers"
                            maxLength={5}
                            {...form.getInputProps("patient.postalCode")}
                        />
                    </Stack>
                )}

                {/* STEP 2: ABOUT YOUR CHILD */}
                {activeStep === 1 && (
                    <Stack gap="sm">
                        <TextInput
                            required
                            label="First Name"
                            placeholder="First Name"
                            size="md"
                            radius="md"
                            {...form.getInputProps("patient.firstName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "patient.firstName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <TextInput
                            required
                            label="Last Name"
                            placeholder="Last Name"
                            size="md"
                            radius="md"
                            {...form.getInputProps("patient.lastName")}
                            onChange={(event) => {
                                form.setFieldValue(
                                    "patient.lastName",
                                    titleCase(event.target.value)
                                );
                            }}
                        />
                        <DateInput
                            required
                            allowDeselect
                            firstDayOfWeek={0}
                            label="Date of Birth"
                            placeholder="Date of Birth"
                            valueFormat="MMM DD, YYYY"
                            size="md"
                            radius="md"
                            defaultDate={new Date()}
                            clearable
                            {...form.getInputProps("patient.dateOfBirth")}
                            getDayProps={(date) => {
                                return {
                                    style: {
                                        backgroundColor: isSameDay(
                                            date,
                                            new Date()
                                        )
                                            ? "var(--mantine-color-primary-7)"
                                            : "inherit"
                                    }
                                };
                            }}
                        />
                        <MultiSelect
                            required
                            variant="pill"
                            label="Therapies Needed"
                            placeholder="Select the services your child needs"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            data={therapyCategories}
                            {...form.getInputProps(
                                "patient.therapyCategoryIds"
                            )}
                            onChange={(value) => {
                                form.setFieldValue(
                                    "patient.therapyCategoryIds",
                                    value
                                );
                                form.setFieldValue(
                                    "patient.therapySubCategoryIds",
                                    []
                                );
                            }}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Skills / Interventions Needed"
                            placeholder="Skills or support your child needs"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            disabled={
                                !form.values.patient?.therapyCategoryIds?.length
                            }
                            data={therapySubCategories ?? []}
                            {...form.getInputProps(
                                "patient.therapySubCategoryIds"
                            )}
                        />
                    </Stack>
                )}

                {/* STEP 3: ADDITIONAL (PREFERENCES + TERMS + FILE UPLOAD) */}
                {activeStep === 2 && (
                    <Stack gap="sm">
                        <Stack gap="sm" mt="md" mb="md">
                            <Input.Label size="md">
                                When Are You Available?
                            </Input.Label>
                            <DayChipGroup
                                abbreviate
                                size="md"
                                value={form.values.patient?.daysAvailable?.map(
                                    (d) => titleCase(d)
                                )}
                                onChipChange={(v: string[]) =>
                                    form.setFieldValue(
                                        "patient.daysAvailable",
                                        v
                                    )
                                }
                            />
                        </Stack>
                        <MultiSelect
                            variant="pill"
                            label="Time Available"
                            placeholder="Time Available"
                            size="md"
                            radius="md"
                            clearable
                            searchable
                            checkIconPosition="right"
                            data={TIMES}
                            {...form.getInputProps("patient.timeAvailable")}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Insurances"
                            placeholder="We prioritize providers that are in-network"
                            size="md"
                            radius="md"
                            limit={100}
                            searchable
                            clearable
                            dropdownOpened={searchInsurances.length > 1}
                            data={insurances}
                            searchValue={searchInsurances}
                            onSearchChange={(value) => {
                                setSearchInsurances(value);
                            }}
                            rightSection={
                                insuranceFetching ? <Loader size={16} /> : null
                            }
                            {...form.getInputProps("patient.insuranceIds")}
                        />
                        <MultiSelect
                            variant="pill"
                            label="Diagnoses"
                            placeholder="Select any that apply, not sure is ok"
                            size="md"
                            radius="md"
                            searchable
                            clearable
                            data={conditions}
                            {...form.getInputProps("patient.conditionIds")}
                        />
                        {/* <MultiSelect
              flex={1}
              variant="pill"
              label="Preferred Languages"
              placeholder="Preferred Languages"
              size="md"
              radius="md"
              data={PREFERRED_LANGUAGES}
              clearable
              searchable
              {...form.getInputProps("patient.preferredLanguages")}
            />
            <Select
              flex={1}
              label="Grade Level"
              placeholder="Grade Level"
              size="md"
              radius="md"
              clearable
              searchable
              data={GRADE_LEVELS}
              {...form.getInputProps("patient.gradeLevel")}
            /> */}
                    </Stack>
                )}

                {/* FOOTER */}
                <Stack mt="lg">
                    {activeStep === 2 && (
                        <Stack gap="lg">
                            <FileUpload
                                files={formFiles}
                                setFiles={handleSetFiles}
                                onRemove={removeFile}
                                onUpdate={updateFileField}
                                fileTypeOptions={FILE_TYPE_OPTIONS}
                            />
                            <Checkbox
                                label={
                                    <>
                                        I accept the{" "}
                                        <Anchor
                                            href="https://therapprove.com/terms/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: "14px",
                                                textDecoration: "underline"
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            terms and conditions
                                        </Anchor>{" "}
                                        and{" "}
                                        <Anchor
                                            href="https://therapprove.com/privacy-policy/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                fontSize: "14px",
                                                textDecoration: "underline"
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            privacy policy
                                        </Anchor>
                                    </>
                                }
                                color="var(--mantine-color-primary-9)"
                                checked={areTermsAndPrivacyPolicyChecked}
                                onChange={(event) => {
                                    setAreTermsAndPrivacyPolicyChecked(
                                        event.currentTarget.checked
                                    );

                                    if (event.currentTarget.checked) {
                                        form.setFieldValue(
                                            "inquiredAt",
                                            new Date().toISOString()
                                        );
                                    }
                                }}
                            />
                        </Stack>
                    )}
                    <Group
                        justify="space-between"
                        align="center"
                        mt="xl"
                        style={{
                            width: "100%"
                        }}
                    >
                        {activeStep > 0 ? (
                            <Button
                                variant="outline"
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                onClick={() =>
                                    setActiveStep((s) => Math.max(0, s - 1))
                                }
                            >
                                Previous
                            </Button>
                        ) : (
                            <div />
                        )}

                        {activeStep < steps.length - 1 ? (
                            <Button
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                onClick={() => setActiveStep(activeStep + 1)}
                            >
                                Next Step
                            </Button>
                        ) : (
                            <Button
                                size="md"
                                style={{ width: "45%", borderRadius: "5rem" }}
                                disabled={isButtonDisabled || isUploadingFiles}
                                loading={isUploadingFiles}
                                onClick={submitPublicPriorityEntry}
                            >
                                Submit
                            </Button>
                        )}
                    </Group>
                </Stack>
            </Stack>
        </Stack>
    );
};
